import { Button, DatePicker, Select, Space, Statistic } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import type { Dayjs } from 'dayjs'
import { EntitySearch } from '@/components/common/EntitySearch'
import { FilterField } from '@/components/common/LookupBar'
import { MovementTable } from '@/components/journey/MovementTable'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import { dashboardService } from '@/services'
import { useRegionStore } from '@/stores/regionStore'
import type { SearchType } from '@/services/dashboard.service'

const { RangePicker } = DatePicker

const TRANSFER_TYPES = [
  'LEAPOUT',
  'LEAPIN',
  'CUSTOUT',
  'CUSTIN',
  'TROUT',
  'TRIN',
  'RELOUT',
  'RELIN',
]

type PartyRole = 'sender' | 'receiver'

export default function PartyJourneyPage() {
  const regionView = useRegionStore((s) => s.view)
  const [params, setParams] = useSearchParams()
  const role = (params.get('role') === 'receiver' ? 'receiver' : 'sender') as PartyRole
  const code = params.get('code') || ''
  const page = Number(params.get('page') || '1')
  const [input, setInput] = useState(code)
  const [transferType, setTransferType] = useState<string | undefined>(
    params.get('transfer_type') || undefined
  )
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)

  useEffect(() => {
    setInput(code)
  }, [code])

  useEffect(() => {
    setTransferType(params.get('transfer_type') || undefined)
  }, [params])

  const searchType: SearchType = role
  const queryParams = {
    page,
    limit: 25,
    transfer_type: transferType || undefined,
    created_at_from: range?.[0]?.format('YYYY-MM-DD'),
    created_at_to: range?.[1]?.format('YYYY-MM-DD'),
  }

  const q = useQuery({
    queryKey: ['party-journey', regionView, role, code, queryParams],
    queryFn: () =>
      role === 'sender'
        ? dashboardService.getSenderJourney(code, queryParams)
        : dashboardService.getReceiverJourney(code, queryParams),
    enabled: Boolean(code),
  })

  const writeParams = (next: {
    role?: PartyRole
    code?: string
    page?: number
    transfer_type?: string | null
  }) => {
    const p: Record<string, string> = {
      role: next.role ?? role,
      page: String(next.page ?? 1),
    }
    const nextCode = next.code !== undefined ? next.code : code
    if (nextCode) p.code = nextCode
    const tt =
      next.transfer_type !== undefined ? next.transfer_type : transferType || null
    if (tt) p.transfer_type = tt
    setParams(p)
  }

  const load = (value: string) => {
    const v = value.trim()
    setInput(v)
    writeParams({ code: v, page: 1 })
  }

  return (
    <PageContainer fluid>
      <PageHeader
        title="Party Journey"
        subtitle="Trace movements for a sender or receiver across documents and tags."
      />

      <SectionCard title="Lookup" subtitle="Choose a party role, search the party, then apply optional filters.">
        <div className="grid gap-3 md:grid-cols-[160px_minmax(0,1fr)] lg:grid-cols-[160px_minmax(0,1fr)_auto] md:items-end">
          <FilterField label="Party role">
            <Select
              className="w-full"
              value={role}
              options={[
                { value: 'sender', label: 'Sender' },
                { value: 'receiver', label: 'Receiver' },
              ]}
              onChange={(v: PartyRole) => {
                writeParams({ role: v, code: input.trim() || undefined, page: 1 })
              }}
            />
          </FilterField>
          <FilterField label={role === 'sender' ? 'Sender' : 'Receiver'}>
            <Space.Compact className="w-full">
              <EntitySearch
                className="w-full"
                type={searchType}
                placeholder={`Search ${role} code or name…`}
                value={input}
                onSelect={load}
                onChange={setInput}
              />
              <Button type="primary" onClick={() => load(input)} disabled={!input.trim()}>
                View
              </Button>
            </Space.Compact>
          </FilterField>
          <div className="filter-bar md:col-span-2 lg:col-span-1 lg:justify-end">
            <FilterField label="Transfer type" className="min-w-[140px]">
              <Select
                allowClear
                className="w-full min-w-[140px]"
                placeholder="Any"
                value={transferType}
                options={TRANSFER_TYPES.map((t) => ({ value: t, label: t }))}
                onChange={(v) => setTransferType(v)}
              />
            </FilterField>
            <FilterField label="Date range">
              <RangePicker
                value={range as [Dayjs, Dayjs] | null}
                onChange={(v) => setRange(v)}
              />
            </FilterField>
            <Button
              disabled={!code}
              onClick={() =>
                writeParams({
                  page: 1,
                  transfer_type: transferType || '',
                })
              }
            >
              Apply
            </Button>
          </div>
        </div>
      </SectionCard>

      {!code ? (
        <SectionCard title="Results">
          <PageEmpty description="Choose Sender or Receiver, search a party code, then View." />
        </SectionCard>
      ) : q.isLoading ? (
        <SectionCard title="Results">
          <PageLoading />
        </SectionCard>
      ) : q.isError ? (
        <PageError message={(q.error as { message?: string })?.message} />
      ) : (
        <SectionCard
          title={`${role === 'sender' ? 'Sender' : 'Receiver'} movements`}
          subtitle={
            [q.data?.partyName || q.data?.partyCode, q.data?.partyCode]
              .filter(Boolean)
              .filter((v, i, a) => a.indexOf(v) === i)
              .join(' · ') || undefined
          }
          actions={
            <Space size="large" wrap>
              <Statistic title="Unique tags" value={q.data?.uniqueTagCount ?? 0} />
              <Statistic title="Unique documents" value={q.data?.uniqueDocumentCount ?? 0} />
            </Space>
          }
          variant="table"
        >
          <MovementTable
            rows={q.data?.history ?? []}
            pagination={{
              page: q.data?.pagination.page ?? 1,
              pageSize: q.data?.pagination.limit ?? 25,
              total: q.data?.pagination.total ?? 0,
            }}
            onPageChange={(p) => writeParams({ page: p })}
          />
        </SectionCard>
      )}
    </PageContainer>
  )
}

export function SenderJourneyPage() {
  const [params] = useSearchParams()
  const qs = new URLSearchParams({ role: 'sender' })
  const code = params.get('code')
  const transferType = params.get('transfer_type')
  if (code) qs.set('code', code)
  if (transferType) qs.set('transfer_type', transferType)
  return <Navigate to={`/party-journey?${qs.toString()}`} replace />
}

export function ReceiverJourneyPage() {
  const [params] = useSearchParams()
  const qs = new URLSearchParams({ role: 'receiver' })
  const code = params.get('code')
  const transferType = params.get('transfer_type')
  if (code) qs.set('code', code)
  if (transferType) qs.set('transfer_type', transferType)
  return <Navigate to={`/party-journey?${qs.toString()}`} replace />
}
